import { Button, GhostButton } from '@mgh-app/component-library';
import { useAuth } from 'auth/useAuth';
import { ValidationErrors } from 'components/ValidationErrors';
import { MainLayoutContext } from 'layouts/MainLayout';
import { useAppNavigation } from 'navigation/useAppNavigation';
import { useContext, useEffect, useState } from 'react';
import OtpInput from 'react-otp-input';
import { clearSignUpData, getSignUpData } from 'slices/SignUpSlice';
import {
  ResendVerificationCodeError,
  SendVerificationCodeError,
  useLazyResendVerificationCodeQuery,
  useLazySendVerificationCodeQuery,
} from 'services/UsersService';
import { useAppDispatch, useAppSelector } from 'store';
import {
  borders,
  display,
  flexDirection,
  fontSize,
  fontWeight,
  gap,
  justifyContent,
  margin,
  outlineWidth,
  textColor,
  twCls,
} from 'style';

const ErrorHelper = {
  verificationCode: {
    required: 'Verification Code is required',
  },
};

export const AccountVerification = (): JSX.Element => {
  const { setTitle, showNavigationButtons, showUserProfile, navigateHomeAction, showHomeButton } =
    useContext(MainLayoutContext);

  const [customError, setCustomError] = useState<string[]>();
  const [customResendError, setCustomResendError] = useState<string[]>();
  const [successMessage, setSuccessMessage] = useState<string[]>();
  const [otp, setOtp] = useState<string>('');

  const [sendVerificationCode] = useLazySendVerificationCodeQuery();
  const [resendCode] = useLazyResendVerificationCodeQuery();
  const { signIn } = useAuth();

  const signUpData = useAppSelector(getSignUpData);
  const dispatch = useAppDispatch();
  const { navigateHome } = useAppNavigation();

  useEffect(() => {
    setTitle('Verification');
    showNavigationButtons(true);
    showUserProfile(false);
    showHomeButton(false);
  }, [navigateHomeAction, setTitle, showHomeButton, showNavigationButtons, showUserProfile]);

  const verify = async () => {
    try {
      setCustomError([]);
      if (!otp) {
        setCustomError([ErrorHelper.verificationCode.required]);
      } else {
        await sendVerificationCode({
          userId: signUpData.email,
          verification_code: otp,
        }).unwrap();
        await signIn(signUpData.email, signUpData.password);

        dispatch(clearSignUpData());
        navigateHome();
      }
    } catch (err) {
      const error = err as SendVerificationCodeError;
      if (error.message) {
        if (Array.isArray(error?.message)) {
          setCustomError(error.message);
        } else if (error?.message) {
          setCustomError([error.message]);
        } else {
          setCustomError(['Unknown error']);
        }
      } else {
        console.error(err);
      }
    }
  };

  const resendVerificationCode = async () => {
    try {
      setCustomResendError([]);
      const result = await resendCode({
        email: signUpData.email,
        isForgotPassword: false,
      }).unwrap();
      setSuccessMessage([result.message]);
    } catch (err) {
      const error = err as ResendVerificationCodeError;
      setCustomResendError([error.message]);
    }
  };

  return (
    <div className={twCls(display('flex'), flexDirection('flex-col'), margin('mt-20'))}>
      <p>A six digit verification code has been sent to your email address</p>
      <OtpInput
        value={otp}
        onChange={setOtp}
        containerStyle={twCls(
          margin('mt-10'),
          display('flex'),
          justifyContent('justify-center'),
          fontSize('text-3xl'),
          fontWeight('font-medium'),
          gap('gap-4'),
        )}
        numInputs={6}
        isInputNum
        inputStyle={twCls(borders('border-b-2'))}
        focusStyle={twCls(outlineWidth('outline-0'))}
        shouldAutoFocus
      />
      <ValidationErrors errors={customError} className={twCls(margin('mt-10'))} />
      <Button className={twCls(margin('mt-4'))} onClick={verify}>
        Verify
      </Button>
      <ValidationErrors
        errors={customResendError || successMessage}
        className={twCls(
          margin('mt-10'),
          customResendError ? textColor('text-red-100') : textColor('text-grey-900'),
        )}
      />
      <GhostButton className={twCls(margin('mt-6'))} onClick={resendVerificationCode}>
        Resend
      </GhostButton>
    </div>
  );
};
